<template>
  <div class="myjokes container">
    <!-- Back link + title -->
    <header class="myjokes__head reveal">
      <router-link to="/profil" class="myjokes__back">← Retour au profil</router-link>
      <h1 class="myjokes__title">✍️ Mes blagues</h1>
      <p class="myjokes__sub">Les blagues que tu as proposées. Tu peux les retoucher.</p>
    </header>

    <!-- Global toast -->
    <transition name="toast">
      <div
        v-if="toast"
        class="toast"
        :class="`toast--${toast.type}`"
        role="status"
      >
        {{ toast.text }}
        <button class="toast__close" aria-label="Fermer" @click="toast = null">✕</button>
      </div>
    </transition>

    <!-- Loading -->
    <div v-if="loading && !jokes.length" class="myjokes__state">
      <span class="spin">🍌</span> On déplie tes blagues…
    </div>

    <!-- Empty -->
    <div v-else-if="!jokes.length" class="myjokes__empty">
      <div class="myjokes__peel">🍌</div>
      <p>Tu n'as pas encore proposé de blague. À toi de jouer !</p>
      <BananaButton to="/proposer" variant="dark" size="sm">Proposer une blague</BananaButton>
    </div>

    <template v-else>
      <ul class="joke-list">
        <li v-for="joke in jokes" :key="joke.id" class="joke-card">
          <div class="joke-card__top">
            <span class="joke-card__score">🍌 {{ joke.score }}</span>
            <span v-if="joke.createdAt" class="joke-card__date">{{ formatDate(joke.createdAt) }}</span>
            <span class="joke-card__id">#{{ joke.id }}</span>
          </div>

          <!-- Inline edit form -->
          <div v-if="editingId === joke.id" class="inline-form">
            <label class="inline-form__label">Nouveau texte</label>
            <textarea v-model="draftText" rows="4" class="field"></textarea>
            <div class="inline-form__actions">
              <BananaButton
                size="sm"
                :loading="busyId === joke.id"
                :disabled="!draftText.trim()"
                @click="saveEdit(joke)"
              >Enregistrer</BananaButton>
              <BananaButton size="sm" variant="ghost" :disabled="busyId === joke.id" @click="cancelEdit">Annuler</BananaButton>
            </div>
          </div>

          <template v-else>
            <router-link :to="`/item/${joke.id}`" class="joke-card__text-link">
              <p class="joke-card__text">{{ joke.text }}</p>
            </router-link>

            <div v-if="joke.tags && joke.tags.length" class="joke-card__tags">
              <span v-for="tag in joke.tags" :key="tag.slug" class="joke-card__tag">{{ tag.label }}</span>
            </div>

            <div class="joke-card__actions">
              <BananaButton size="sm" variant="outline" @click="startEdit(joke)">✏️ Modifier</BananaButton>
            </div>
          </template>
        </li>
      </ul>

      <!-- Load more -->
      <div v-if="canLoadMore" class="joke-more">
        <BananaButton variant="outline" :loading="loading" @click="loadMore">
          Voir plus
        </BananaButton>
      </div>
    </template>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import { MY_JOKES_QUERY, EDIT_MY_JOKE_MUTATION } from '@/graphql/jokes'
import { authState } from '@/auth'

const PAGE_SIZE = 12

export default {
  name: 'ProfileJokesView',
  components: { BananaButton },
  data() {
    return {
      authState,
      jokes: [],
      loading: true,
      offset: 0,
      canLoadMore: false,
      editingId: null,
      draftText: '',
      busyId: null,
      toast: null,
      toastTimer: null,
    }
  },
  computed: {
    user() {
      return this.authState.user
    },
  },
  created() {
    if (this.user) this.fetchJokes()
    else this.loading = false
  },
  methods: {
    async fetchJokes() {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: MY_JOKES_QUERY,
          variables: { count: PAGE_SIZE, offset: this.offset },
          fetchPolicy: 'network-only',
        })
        const batch = (data && data.myJokes) || []
        this.jokes = this.offset === 0 ? batch : this.jokes.concat(batch)
        this.canLoadMore = batch.length === PAGE_SIZE
      } catch (e) {
        console.error('Error fetching my jokes:', e)
        this.canLoadMore = false
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.offset += PAGE_SIZE
      this.fetchJokes()
    },
    startEdit(joke) {
      this.editingId = joke.id
      this.draftText = joke.text
    },
    cancelEdit() {
      this.editingId = null
      this.draftText = ''
    },
    async saveEdit(joke) {
      const newText = this.draftText.trim()
      if (!newText) return
      this.busyId = joke.id
      try {
        const { data } = await this.$apollo.mutate({
          mutation: EDIT_MY_JOKE_MUTATION,
          variables: { jokeId: parseInt(joke.id, 10), newText },
        })
        // Backend shape: editMyJoke { joke { id text } }
        const updated = data && data.editMyJoke && data.editMyJoke.joke
        joke.text = (updated && updated.text) || newText
        this.notify('Blague mise à jour ✏️')
        this.cancelEdit()
      } catch (e) {
        this.gqlError(e)
      } finally {
        this.busyId = null
      }
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(iso))
      } catch { return '' }
    },
    notify(text, type = 'success') {
      this.toast = { text, type }
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toast = null }, 4000)
    },
    gqlError(e) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Une erreur est survenue.'
      this.notify(msg, 'error')
    },
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer)
  },
}
</script>

<style scoped>
.myjokes { padding: 40px 20px 70px; max-width: 760px; }

/* Header */
.myjokes__head { margin-bottom: 24px; }
.myjokes__back {
  display: inline-block;
  margin-bottom: 14px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-700);
  text-decoration: none;
}
.myjokes__back:hover { color: var(--ink-900); text-decoration: underline; }
.myjokes__title { font-size: clamp(1.7rem, 5vw, 2.3rem); margin: 0 0 6px; }
.myjokes__sub { color: var(--text-soft); margin: 0; }

/* States */
.myjokes__state {
  text-align: center; padding: 40px; color: var(--text-soft);
  font-family: var(--font-accent); font-style: italic; font-size: 1.05rem;
}
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.myjokes__empty {
  text-align: center; padding: 34px 20px;
  border: 2px dashed color-mix(in srgb, var(--ink-900) 25%, transparent);
  border-radius: var(--r-lg);
}
.myjokes__empty p { color: var(--text-soft); margin: 0 auto 18px; max-width: 400px; }
.myjokes__peel { font-size: 2.6rem; margin-bottom: 10px; }

/* Joke list */
.joke-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.joke-card {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-pop);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.joke-card__top { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 0.85rem; font-weight: 600; color: var(--text-soft); }
.joke-card__id {
  margin-left: auto;
  font-family: var(--font-display);
  font-weight: 800;
  color: var(--banana-700);
}
.joke-card__text-link { text-decoration: none; color: var(--ink-900); }
.joke-card__text {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.joke-card__text-link:hover .joke-card__text { color: var(--banana-800); }

.joke-card__tags { display: flex; flex-wrap: wrap; gap: 8px; }
.joke-card__tag {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.74rem;
  padding: 3px 10px;
  border-radius: var(--r-pill);
  border: 1.5px solid var(--border-c);
  background: var(--banana-100);
  color: var(--ink-800);
}

.joke-card__actions { display: flex; gap: 10px; flex-wrap: wrap; }
.joke-more { text-align: center; margin-top: 24px; }

/* Inline edit form (mirrors Admin) */
.inline-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: var(--banana-50);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
}
.inline-form__label {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--ink-800);
}
.inline-form__actions { display: flex; gap: 10px; flex-wrap: wrap; }
.field {
  font-family: var(--font-body);
  font-size: 1rem;
  padding: 10px 12px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-sm);
  background: var(--cream);
  color: var(--ink-900);
  width: 100%;
  resize: vertical;
}
.field:focus-visible { outline: 3px solid var(--banana-500); outline-offset: 1px; }

/* Toast */
.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: var(--font-display);
  font-weight: 700;
  padding: 12px 18px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-pop);
  max-width: 90vw;
}
.toast--success { background: var(--banana-400); color: var(--ink-900); }
.toast--error { background: var(--bruise); color: #fff; }
.toast__close { background: none; border: none; cursor: pointer; color: inherit; font-size: 0.9rem; }
.toast-enter-active, .toast-leave-active { transition: all 0.3s var(--ease-bounce); }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translate(-50%, 16px); }

@media (max-width: 560px) {
  .myjokes { padding: 30px 20px 50px; }
}
</style>
